import { RulerLine, RulerElementState, createRulerLineId } from "./models/RulerLine";
import type { RulerModeId } from "./models/RulerMode";
import { scopeStringDevStorageKey } from "../core/StringDevStorageScope";

const STORAGE_PREFIX = "string-rulers:";

/**
 * Centralized state manager for ruler lines across all elements.
 *
 * Responsibilities:
 * - Per-element, per-mode RulerLine CRUD
 * - Persistence to / from localStorage
 */
export class RulersManager {
  private elementStates: Map<string, RulerElementState> = new Map();

  private stateKey(elementId: string, modeId: RulerModeId = "default"): string {
    return `${elementId}:${modeId}`;
  }

  private storageKey(elementId: string, modeId: RulerModeId = "default"): string {
    return scopeStringDevStorageKey(STORAGE_PREFIX + elementId + ":" + modeId);
  }

  private legacyStorageKey(elementId: string): string {
    return scopeStringDevStorageKey(STORAGE_PREFIX + elementId);
  }

  // ── Line Management ───────────────────────────────────

  getLines(elementId: string, modeId: RulerModeId = "default"): RulerLine[] {
    return this.elementStates.get(this.stateKey(elementId, modeId))?.lines ?? [];
  }

  addLine(
    elementId: string,
    axis: "horizontal" | "vertical",
    position: number,
    targetLabel?: string,
    modeId: RulerModeId = "default",
    targetSelector?: string,
    targetEdge?: RulerLine["targetEdge"],
  ): RulerLine {
    const line: RulerLine = { id: createRulerLineId(), axis, position, targetLabel, targetSelector, targetEdge };
    const key = this.stateKey(elementId, modeId);

    let state = this.elementStates.get(key);
    if (!state) {
      state = { elementId, lines: [] };
      this.elementStates.set(key, state);
    }

    state.lines.push(line);
    this.save(elementId, modeId);
    return line;
  }

  /**
   * Updates the position of an existing line (called while dragging).
   * Does NOT persist on every move — call `save()` explicitly when drag ends.
   */
  moveLine(
    elementId: string,
    lineId: string,
    position: number,
    targetLabel?: string,
    modeId: RulerModeId = "default",
  ): void {
    const line = this.findLine(elementId, lineId, modeId);
    if (!line) return;
    line.position = position;
    line.targetLabel = targetLabel;
  }

  removeLine(elementId: string, lineId: string, modeId: RulerModeId = "default"): void {
    const state = this.elementStates.get(this.stateKey(elementId, modeId));
    if (!state) return;
    state.lines = state.lines.filter((l) => l.id !== lineId);
    this.save(elementId, modeId);
  }

  removeAllLines(elementId: string, modeId: RulerModeId = "default"): void {
    this.elementStates.delete(this.stateKey(elementId, modeId));
    try {
      localStorage.removeItem(this.storageKey(elementId, modeId));
      if (modeId === "default") {
        localStorage.removeItem(this.legacyStorageKey(elementId));
      }
    } catch {
      // localStorage may be unavailable
    }
  }

  findLine(elementId: string, lineId: string, modeId: RulerModeId = "default"): RulerLine | undefined {
    return this.elementStates.get(this.stateKey(elementId, modeId))?.lines.find((l) => l.id === lineId);
  }

  // ── Persistence ───────────────────────────────────────

  save(elementId: string, modeId: RulerModeId = "default"): void {
    const state = this.elementStates.get(this.stateKey(elementId, modeId));
    if (!state) return;
    try {
      const serialized = JSON.stringify(state.lines);
      localStorage.setItem(this.storageKey(elementId, modeId), serialized);
      if (modeId === "default") {
        localStorage.setItem(this.legacyStorageKey(elementId), serialized);
      }
    } catch {
      // localStorage may be unavailable
    }
  }

  load(elementId: string, modeId: RulerModeId = "default"): void {
    const key = this.stateKey(elementId, modeId);
    try {
      let raw = localStorage.getItem(this.storageKey(elementId, modeId));

      // Migration: if mode-qualified key is absent and this is "default",
      // try the old unqualified key (pre-modes format).
      if (!raw && modeId === "default") {
        raw = localStorage.getItem(this.legacyStorageKey(elementId));
      }

      if (!raw) return;
      const lines: RulerLine[] = JSON.parse(raw);
      if (!Array.isArray(lines)) return;
      this.elementStates.set(key, { elementId, lines });
    } catch {
      // ignore corrupt data
    }
  }

  clearElement(elementId: string): void {
    for (const [key] of this.elementStates) {
      if (key.startsWith(elementId + ":")) {
        this.elementStates.delete(key);
      }
    }
  }

  clearAll(): void {
    this.elementStates.clear();
  }
}
